/**
 * lod_fade — the crossfade rule (LOD-switch research S5, 2026-09-02).
 *
 * A building's incoming and outgoing levels are drawn together for
 * FADE_MS with COMPLEMENTARY screen-door patterns (Cesium for Unreal's
 * dithered opacity mask): each fragment computes one noise value n in [0,1)
 * from its screen position; the incoming level keeps the fragment when
 * n < t, the outgoing level when n ≥ t, so every pixel shows exactly one of
 * the two at every instant — no double coverage, no holes. Per instance the
 * value is packed into one float `aFade`: t ∈ [0, 1] = incoming (1 = solid,
 * the steady state), t − 2 ∈ [−2, −1] = outgoing (the offset keeps the two
 * ranges unambiguous — a plain −t is −0 at the start of a fade, which every
 * comparison reads as +0 and both levels dropped every pixel for a frame).
 * This file is the JavaScript mirror of the TSL in lib/instancing.ts; the
 * harness pins it.
 */
export const FADE_MS = 320;

/** Interleaved gradient noise (Jimenez 2014) for a pixel position. */
export function ignNoise(x: number, y: number): number {
  const f = (v: number): number => v - Math.floor(v);
  return f(52.9829189 * f(0.06711056 * x + 0.00583715 * y));
}

/** Keep this fragment? `fade` ≥ −0.5: incoming, keep when noise < fade (1 = always); `fade` < −0.5: outgoing at t = fade + 2, keep when noise ≥ t. */
export function fadeKeep(noise: number, fade: number): boolean {
  return fade < -0.5 ? noise >= fade + 2 : noise < fade;
}

/** The fade values for a crossfade at progress t ∈ [0,1]: incoming t, outgoing t − 2. */
export function fadePair(t: number): { incoming: number; outgoing: number } {
  const c = Math.max(0, Math.min(1, t));
  return { incoming: c, outgoing: c - 2 };
}
